import React, { useState } from 'react';
import { Box, Button, TextField, Paper, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { UsersTokenPlanTokenInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getUsersTokenPlanTokensColumns } from './UsersTokenPlanTokensColumns';
import UsersUserTokenDialog from './UsersUseTokenDialog';
import { UsersAddTokensDialog } from './UsersAddTokenDialog';

const UsersTokenPlanTokensAdminList: React.FC = () => {
  const [tokens, setTokens] = useState<UsersTokenPlanTokenInterface[]>([]);
  const [userId, setUserId] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [addTokensDialogOpen, setAddTokensDialogOpen] = useState(false);
  const [useTokenDialogOpen, setUseTokenDialogOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<UsersTokenPlanTokenInterface | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | string | undefined>(undefined);

  // Fetch tokens by userId
  const handleGetTokensByUserId = async () => {
    setAlert(null);
    setTokens([]);
    if (!userId) {
      setAlert({ type: 'error', message: 'Please enter a User ID' });
      return;
    }
    try {
      const result = await apiGet<GenericResponseInterface<UsersTokenPlanTokenInterface[]>>(`/usersTokenPlanTokens/user/${userId}`);
      if (result.status && result.data) {
        setTokens(result.data);
        if (result.data.length === 0) {
          setAlert({ type: 'success', message: 'No tokens found for this user.' });
        }
      } else {
        setAlert({ type: 'error', message: result.message || 'Failed to fetch tokens' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to fetch tokens' });
    }
  };

  // Use token handler (opens dialog)
  const handleUseTokenClick = (row: UsersTokenPlanTokenInterface) => {
    setSelectedToken(row);
    setUseTokenDialogOpen(true);
  };

  // Actually use token (from dialog)
  const handleUseToken = async (data: { usedAt: string; notes: string; usedFor: string }) => {
    setAlert(null);
    setUseTokenDialogOpen(false);
    if (!selectedToken) return;
    try {
      const result = await apiRequest<GenericResponseInterface>(
        `/usersTokenPlanTokens/use/${selectedToken.userId}`,
        'POST',
        {
          tokenId: selectedToken.id,
          usedAt: data.usedAt,
          notes: data.notes,
          usedFor: data.usedFor || '{}',
        }
      );
      if (result.status) {
        setAlert({ type: 'success', message: result.message || 'Token used successfully!' });
        handleGetTokensByUserId();
      } else {
        setAlert({ type: 'error', message: result.message || 'Failed to use token' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to use token' });
    }
  };

  // Restore token handler
  const handleRestoreToken = async (row: UsersTokenPlanTokenInterface) => {
    setAlert(null);
    try {
      const result = await apiRequest<GenericResponseInterface>(`/usersTokenPlanTokens/restore/${row.id}`, 'POST');
      if (result.status) {
        setAlert({ type: 'success', message: result.message || 'Token restored successfully!' });
        handleGetTokensByUserId();
      } else {
        setAlert({ type: 'error', message: result.message || 'Failed to restore token' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to restore token' });
    }
  };

  // Add tokens handler (opens dialog)
  const handleAddTokensClick = (row: UsersTokenPlanTokenInterface) => {
    setSelectedUserId(row.userId);
    setAddTokensDialogOpen(true);
  };

  // Actually add tokens
  const handleAddTokensTokens = async (tokenCount: number) => {
    setAlert(null);
    setAddTokensDialogOpen(false);
    if (!selectedUserId) return;
    try {
      const result = await apiRequest<GenericResponseInterface>(`/usersTokenPlanTokens/insert/user/${selectedUserId}/${tokenCount}`, 'POST');
      if (result.status) {
        setAlert({ type: 'success', message: result.message || 'Tokens added successfully!' });
        handleGetTokensByUserId();
      } else {
        setAlert({ type: 'error', message: result.message || 'Failed to add tokens' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to add tokens' });
    }
  };

  const columns = getUsersTokenPlanTokensColumns(handleUseTokenClick, handleRestoreToken, handleAddTokensClick);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="User ID"
          value={userId}
          onChange={e => setUserId(e.target.value)}
          size="small"
        />
        <Button variant="contained" onClick={handleGetTokensByUserId}>
          Get Tokens by User ID
        </Button>
      </Box>
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}
      <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
        {tokens.length > 0 && (
          <DataGrid
            rows={tokens.map(t => ({ ...t, id: t.id ?? Math.random() }))}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } }
            }}
            pageSizeOptions={[10, 25, 50]}
            autoHeight
          />
        )}
      </Paper>
      <UsersUserTokenDialog
        open={useTokenDialogOpen}
        userId={selectedToken?.userId}
        tokenId={selectedToken?.id}
        onClose={() => setUseTokenDialogOpen(false)}
        onSubmit={handleUseToken}
      />
      <UsersAddTokensDialog
        open={addTokensDialogOpen}
        userId={selectedUserId}
        onClose={() => setAddTokensDialogOpen(false)}
        onSubmit={handleAddTokensTokens}
      />
    </Box>
  );
};

export default UsersTokenPlanTokensAdminList;